import "@/App.css";
import { useEffect } from "react";
import {
    BrowserRouter,
    Routes,
    Route,
    useParams,
    Navigate,
} from "react-router-dom";
import { Toaster } from "sonner";
import { useTranslation } from "react-i18next";

import { SUPPORTED_LANG_CODES } from "@/i18n";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";

import Home from "@/pages/Home";
import About from "@/pages/About";
import Products from "@/pages/Products";
import Process from "@/pages/Process";
import Export from "@/pages/Export";
import Gallery from "@/pages/Gallery";
import Contact from "@/pages/Contact";
import NotFound from "@/pages/NotFound";

const PAGES = [
    { path: "", element: <Home /> },
    { path: "about", element: <About /> },
    { path: "products", element: <Products /> },
    { path: "process", element: <Process /> },
    { path: "export", element: <Export /> },
    { path: "gallery", element: <Gallery /> },
    { path: "contact", element: <Contact /> },
];

// Syncs i18n + <html lang> to the :lang URL segment so a shared link always
// renders in the language it advertises, regardless of stored preference.
const LocaleSync = ({ children }) => {
    const { lang } = useParams();
    const { i18n } = useTranslation();
    const target = lang && SUPPORTED_LANG_CODES.includes(lang) ? lang : "en";

    useEffect(() => {
        if (i18n.language !== target) i18n.changeLanguage(target);
        document.documentElement.lang = target;
    }, [target, i18n]);

    if (lang && !SUPPORTED_LANG_CODES.includes(lang)) return <NotFound />;
    return children;
};

function App() {
    return (
        <div className="App" data-testid="app-root">
            <BrowserRouter>
                <Navbar />
                <Routes>
                    {/* English at the root, preserving existing indexed URLs */}
                    {PAGES.map((p) => (
                        <Route
                            key={`en-${p.path}`}
                            path={`/${p.path}`}
                            element={<LocaleSync>{p.element}</LocaleSync>}
                        />
                    ))}

                    {/* Other languages under a /:lang prefix */}
                    <Route path="/:lang">
                        {PAGES.map((p) => (
                            <Route
                                key={`i18n-${p.path}`}
                                index={p.path === ""}
                                path={p.path || undefined}
                                element={<LocaleSync>{p.element}</LocaleSync>}
                            />
                        ))}
                    </Route>

                    <Route path="/index.html" element={<Navigate to="/" replace />} />
                    <Route path="*" element={<NotFound />} />
                </Routes>
                <Footer />
                <WhatsAppFloat />
                <Toaster
                    theme="dark"
                    position="bottom-center"
                    toastOptions={{
                        style: {
                            background: "#1A1A1A",
                            color: "#E8E3D9",
                            border: "1px solid #2C2C2C",
                            borderRadius: "0",
                            fontFamily: "Manrope, sans-serif",
                        },
                    }}
                />
            </BrowserRouter>
        </div>
    );
}

export default App;
